import { FluentBundle } from 'fluent';
import {
  GetString,
} from 'fluent-react';
import React from 'react';
import {
  LookBackYearRange,
} from '../../graphQL/types/shared';
import PresentationManager from '../../presentationManager/PresentationManager';
import { VizType } from '../../viz/Utils';
import CountrySelector from '../countrySelector/InteriorCountrySelector';
import ExploreEngageButtons from '../exploreEngageButtons';
import {
  TradeFlowSelectorContainer,
} from '../exportBasket';
import LeftPanelBoxShadow from '../exportBasket/LeftPanelBoxShadow';
import {
  ComplexitySelectorContainer,
} from '../exportComplexity';
import Chart from '../exportGrowthDynamicsChart';
import Legend from '../exportGrowthDynamicsLegend';
import GraphControlDropdown from '../graphControlDropdown';
import {
  IOption,
} from '../graphControlDropdown';
import {
  Root,
} from '../Grid';
import HamburgerMenu from '../menu';
import SectionNav from '../sectionNav';
import Sidebar from '../sidebar';
import UtilityBar from '../utilityBarItems';
import {chapters} from '../utilityBarItems/VideoTutorial';
import {
  CommonElementName,
  defaultFadeTransition,
  defaultVerticalSlideTransition,
  ExportsChangeNodeSizing,
  getUniqueSlideElementKey,
  SlideName,
} from '../Utils';
import SectorLabels from './SectorLabels';
import TopPageDropShadow from './TopPageDropShadow';

const getExportChangeNodeSizingOptions =
  (getFluentString: GetString): Array<IOption<ExportsChangeNodeSizing>> => ([
    {
      value: ExportsChangeNodeSizing.CountryTrade,
      label: getFluentString('growth-dynamics-node-sizing-country-trade'),
      isEnabled: true,
    },
    {
      value: ExportsChangeNodeSizing.WorldTrade,
      label: getFluentString('growth-dynamics-node-sizing-world-trade'),
      isEnabled: true,
    },
    {
      value: ExportsChangeNodeSizing.None,
      label: getFluentString('growth-dynamics-node-sizing-none'),
      isEnabled: true,
    },
  ]);
const getExportChangeYearRangeOptions =
  (getFluentString: GetString): Array<IOption<LookBackYearRange>> => ([
  {
    value: LookBackYearRange.ThreeYears,
    label: getFluentString('growth-dyanmics-year-range-selector-option', {quantity: 3}),
    isEnabled: true,
  },
  {
    value: LookBackYearRange.FiveYears,
    label: getFluentString('growth-dyanmics-year-range-selector-option', {quantity: 5}),
    isEnabled: true,
  },
  {
    value: LookBackYearRange.TenYears,
    label: getFluentString('growth-dyanmics-year-range-selector-option', {quantity: 10}),
    isEnabled: true,
  },
]);

interface Input {
  getFluentString: GetString;
  countryId: string;
  year: number;
  selectCountry: (country: string) => void;
  slideIndex: number;
  setSlideIndex: (index: number) => void;
  firstContentfulSlideIndex: number;
  exportChangeYearRange: LookBackYearRange;
  setExportChangeYearRange: (value: LookBackYearRange) => void;
  exportChangeNodeSizing: ExportsChangeNodeSizing;
  setExportChangeNodeSizing: (value: ExportsChangeNodeSizing) => void;
  goToNextSlide: () => void;
  goToPrevSlide: () => void;
  fluentBundle: FluentBundle;
  yearRangeDropdownZIndex: number;
  nodeSizingDropdownZIndex: number;
  exportGrowthDynamicsLegendZIndex: number;
  sectorLabelsZIndex: number;
  hamburgerMenuZIndex: number;
  sidebarZIndex: number;
  countrySelectorZIndex: number;
  sectionNavZIndex: number;
  topOfPageDropShadowZIndex: number;
  chartZIndex: number;
  exploreEngageButtonsZIndex: number;
  helpTipsTriggerZIndex: number;
  toggleHelpTooltips: () => void;
  helpTooltipsActive: boolean;
  promotedZIndex: number;
}

const getExportGrowthDynamicsSlide = (input: Input) => {
  const {
    countryId, year, selectCountry, slideIndex, setSlideIndex,
    firstContentfulSlideIndex,
    exportChangeYearRange, setExportChangeYearRange,
    exportChangeNodeSizing, setExportChangeNodeSizing,
    yearRangeDropdownZIndex, promotedZIndex, nodeSizingDropdownZIndex,
    goToNextSlide, goToPrevSlide, fluentBundle, exportGrowthDynamicsLegendZIndex,
    sectorLabelsZIndex, hamburgerMenuZIndex, sidebarZIndex,
    countrySelectorZIndex, sectionNavZIndex, topOfPageDropShadowZIndex, chartZIndex,
    getFluentString, exploreEngageButtonsZIndex, helpTipsTriggerZIndex, toggleHelpTooltips,
    helpTooltipsActive,
  } = input;

  const helpTooltipOrder = {
    dropdown: 1,
    sectionNav: 2,
    pageAdvancer: 3,
    exploreButton: 4,
    sectorLabels: 5,
  };

  return (
    <PresentationManager RootComponent={Root} slideIndex={slideIndex}>
      <CountrySelector
        selectedCountry={countryId}
        onCountrySelect={selectCountry}
        promptText={getFluentString('country-selector-prompt')}
        getFluentString={getFluentString}
        zIndex={countrySelectorZIndex}
        transition={defaultVerticalSlideTransition}
        key={CommonElementName.CountrySelector}
        isHelpActive={helpTooltipsActive}
        helpTooltipIndex={helpTooltipOrder.dropdown}
      />
      <SectionNav
        startingIndexOfFirstSection={0}
        slideIndex={slideIndex}
        setSlideIndex={setSlideIndex}
        year={year}
        getFluentString={getFluentString}
        countryId={countryId}
        zIndex={sectionNavZIndex}
        key={CommonElementName.SectionNav}
        transition={defaultVerticalSlideTransition}
        isHelpActive={helpTooltipsActive}
        helpTooltipIndex={helpTooltipOrder.sectionNav}
      />
      <Sidebar
        firstContentfulSlideIndex={firstContentfulSlideIndex}
        slideIndex={slideIndex}
        getFluentString={getFluentString}
        goToNextSlide={goToNextSlide}
        goToPrevSlide={goToPrevSlide}
        countryId={countryId}
        fluentBundle={fluentBundle}
        zIndex={sidebarZIndex}
        yearRange={exportChangeYearRange}
        transition={defaultVerticalSlideTransition}
        key={CommonElementName.Sidebar}
        isHelpActive={helpTooltipsActive}
        helpTooltipIndex={helpTooltipOrder.pageAdvancer}
      />
      <LeftPanelBoxShadow
        transition={defaultVerticalSlideTransition}
        key={CommonElementName.LeftPanelBackground}
      />
      <TopPageDropShadow
        transition={defaultFadeTransition}
        zIndex={topOfPageDropShadowZIndex}
        key={getUniqueSlideElementKey(SlideName.ExportBasket, 'top-page-drop-shadow')}
      />
      <HamburgerMenu
        getFluentString={getFluentString}
        zIndex={hamburgerMenuZIndex}
        transition={defaultFadeTransition}
        key={CommonElementName.HamburgerMenu}
      />
      <UtilityBar
        getFluentString={getFluentString}
        zIndex={helpTipsTriggerZIndex}
        toggleHelpTooltips={toggleHelpTooltips}
        helpTooltipsActive={helpTooltipsActive}
        transition={defaultFadeTransition}
        countryId={countryId}
        videoChapter={chapters[4]}
        key={CommonElementName.HelpTipsTrigger}
      />
      <GraphControlDropdown
        label={getFluentString('growth-dyanmics-year-range-selector-label')}
        selectedValue={exportChangeYearRange}
        options={getExportChangeYearRangeOptions(getFluentString)}
        onValueSelect={setExportChangeYearRange}
        zIndex={yearRangeDropdownZIndex}
        Container={TradeFlowSelectorContainer}
        promotedZIndex={promotedZIndex}
        transition={defaultFadeTransition}
        key={getUniqueSlideElementKey(SlideName.ExportGrowthDynamics, 'year-range-selector')}
      />
      <GraphControlDropdown
        label={getFluentString('growth-dynamics-node-sizing-label')}
        selectedValue={exportChangeNodeSizing}
        options={getExportChangeNodeSizingOptions(getFluentString)}
        onValueSelect={setExportChangeNodeSizing}
        zIndex={nodeSizingDropdownZIndex}
        Container={ComplexitySelectorContainer}
        promotedZIndex={promotedZIndex}
        transition={defaultFadeTransition}
        key={getUniqueSlideElementKey(SlideName.ExportGrowthDynamics, 'node-sizing-selector')}
      />
      <ExploreEngageButtons
        zIndex={exploreEngageButtonsZIndex}
        year={year}
        countryId={countryId}
        vizType={VizType.Stack}
        getFluentString={getFluentString}
        transition={defaultFadeTransition}
        key={CommonElementName.ExploreEngageButtons}
        isHelpActive={helpTooltipsActive}
        helpTooltipIndex={helpTooltipOrder.exploreButton}
      />
      <Legend
        year={year}
        location={countryId}
        yearRange={exportChangeYearRange}
        zIndex={exportGrowthDynamicsLegendZIndex}
        getFluentString={getFluentString}
        nodeSizing={exportChangeNodeSizing}
        transition={defaultFadeTransition}
        key={getUniqueSlideElementKey(SlideName.ExportGrowthDynamics, 'chart-legend')}
      />
      <Chart
        year={year}
        location={countryId}
        yearRange={exportChangeYearRange}
        getFluentString={getFluentString}
        zIndex={chartZIndex}
        transition={defaultFadeTransition}
        fluentBundle={fluentBundle}
        nodeSizing={exportChangeNodeSizing}
        key={getUniqueSlideElementKey(SlideName.ExportGrowthDynamics, 'content')}
      />
      <SectorLabels
        isServicesEnabled={true}
        zIndex={sectorLabelsZIndex}
        transition={defaultFadeTransition}
        key={getUniqueSlideElementKey(SlideName.ExportGrowthDynamics, 'sector-labels')}
        isHelpActive={helpTooltipsActive}
        helpTooltipIndex={helpTooltipOrder.sectorLabels}
        getFluentString={getFluentString}
      />

    </PresentationManager>
  );
};

export default getExportGrowthDynamicsSlide;
